import type { FC } from 'react';
import type { UnitToggleConfig } from '../../../config/tpoGenConfig';
import { tpoOptionTestId, tpoStateAttrs } from '../../../config/testIds';

interface UnitToggleProps {
    config: UnitToggleConfig;
    value: string | undefined;
    onChange: (value: string) => void;
    disabled: boolean;
    /** Base id for QA locators. Per-option ids derive from it. */
    testId?: string;
}

const UnitToggle: FC<UnitToggleProps> = ({ config, value, onChange, disabled, testId }) => {
    const activeValue = value ?? config.defaultValue ?? config.options[0]?.value;
    return (
        <div className="flex items-center gap-1 select-none" data-testid={testId}>
            {config.options.map((opt) => {
                const isActive = activeValue === opt.value;
                // Per-option lock comes from the engine (`disableByMap`); the whole
                // toggle can also be disabled via the parent field.
                const optDisabled = disabled || !!opt.disabled;
                return (
                    <span
                        key={opt.value}
                        data-testid={testId ? tpoOptionTestId(testId, opt.value) : undefined}
                        data-state-active={isActive ? 'true' : undefined}
                        {...tpoStateAttrs({ disabled: optDisabled })}
                        onClick={() => {
                            if (optDisabled) return;
                            onChange(opt.value);
                        }}
                        className={`px-1 text-xs ${isActive ? 'font-bold text-purpleFont' : 'text-gray-400'} ${
                            optDisabled ? 'cursor-not-allowed opacity-20' : 'cursor-pointer'
                        }`}
                    >
                        {opt.label}
                    </span>
                );
            })}
        </div>
    );
};

UnitToggle.displayName = 'UnitToggle';

export default UnitToggle;
